/**
 * TCG Universal Configurator — Shopify Initializer
 *
 * Embeds the TCG Configurator (Photo Books, Multi-Image, Design editors)
 * into Shopify via an iframe with postMessage communication.
 *
 * Handles:
 * - iframe URL construction with embed params
 * - postMessage bridge (tcg:* events from/to iframe)
 * - Shopify cart integration via /cart/add.js
 * - Cart item editing (replace existing line items)
 * - Auto-resize iframe to content height
 */
(function (global) {
  'use strict';

  var instances = new Map(); // containerId -> { iframe, onMessage, config }

  // =========================================================================
  // HELPERS
  // =========================================================================

  function getUrlParam(key) {
    try {
      return new URLSearchParams(global.location.search || '').get(key) || '';
    } catch (_) {
      return '';
    }
  }

  /**
   * Build the iframe src URL based on editor type and config.
   */
  function buildIframeSrc(cfg) {
    var params = new URLSearchParams();
    params.set('embed', 'true');
    params.set('platform', 'shopify');
    params.set('shop', cfg.shopCode || '');
    params.set('lang', cfg.locale || '');
    params.set('currency', cfg.currency || '');
    params.set('country', cfg.country || '');
    params.set('apiUrl', cfg.apiUrl || '');
    params.set('parentUrl', global.location.href);

    // Pass through URL params from current page.
    //
    // A param NOT in this list is lost: it survives only inside `parentUrl`, which is the
    // whole href percent-encoded, and the editor does not parse its own params back out of
    // it. `bindingType` was missing, so `…/editor?editorType=photobook&bindingType=HARDCOVER`
    // always landed on the generic cover selection (Emils, 2026-08-07) — the deep links on
    // the photobook binding cards were dead for exactly this reason.
    //
    // Deliberately still absent: `discountCode`, `env`, `adminPreviewToken`, `debug`,
    // `nudgeDelayMs`. The editor reads all of them, but nothing on the Shopify side sets
    // them today; add one when a caller actually needs it rather than forwarding the whole
    // query string, which would let any URL param reach the editor.
    [
      'productCode',
      'configurationId',
      'cartItemId',
      'variantId',
      'layoutId',
      'category',
      'subtype',
      'bindingType',
    ].forEach(function (k) {
      var v = getUrlParam(k) || cfg[k];
      if (v) params.set(k, v);
    });

    // Default product code from section settings
    if (!params.get('productCode') && cfg.productCode) {
      params.set('productCode', cfg.productCode);
    }

    // Shopify customer ID for saved projects
    if (cfg.shopifyCustomerId) {
      params.set('shopifyCustomerId', cfg.shopifyCustomerId);
    }

    // Determine iframe path based on editor type
    // URL param ?editorType= overrides section setting (allows one page for multiple editors)
    // URL param ?subtype= also maps to design editor type (e.g. ?subtype=citymap → design-citymap)
    var configId = params.get('configurationId');
    var urlEditorType = getUrlParam('editorType');
    var urlSubtype = getUrlParam('subtype');
    var editorType =
      urlEditorType || (urlSubtype ? 'design-' + urlSubtype : '') || cfg.editorType || 'photobook';
    var path;

    switch (editorType) {
      case 'photobook':
        path = configId ? '/editor/' + encodeURIComponent(configId) : '/photobooks';
        break;
      case 'calendar':
        path = configId ? '/editor/' + encodeURIComponent(configId) : '/calendars';
        break;
      case 'photocard':
        path = configId ? '/editor/' + encodeURIComponent(configId) : '/photo-cards';
        break;
      case 'single-upload':
        path = '/editor/single-upload';
        break;
      case 'multi-image':
        path = '/editor/multi-image';
        break;
      case 'ai-products':
        path = '/editor/ai';
        break;
      case 'design-starmap':
      case 'design-citymap':
      case 'design-typo':
        params.set('subtype', editorType.replace('design-', ''));
        path = '/editor/design';
        break;
      case 'design-layouts':
        var layoutId = params.get('layoutId');
        var category = params.get('category');
        if (layoutId || configId) {
          path = '/editor/design-layout';
        } else if (category) {
          path = '/design-layouts/' + encodeURIComponent(category);
        } else {
          path = '/design-layouts';
        }
        break;
      default:
        path = '/editor/' + editorType;
    }

    return cfg.appUrl.replace(/\/+$/, '') + path + '?' + params.toString();
  }

  // =========================================================================
  // SKU → VARIANT LOOKUP (Storefront API)
  // =========================================================================

  // A Map, not a plain object: the key is a SKU from the editor's postMessage payload, so an
  // attacker-supplied `__proto__` / `constructor` would write onto Object.prototype instead
  // of into the cache (CodeQL "remote property injection"). `instances` above is already a
  // Map for the same reason.
  var variantCache = new Map();
  var STOREFRONT_API_VERSION = '2025-07';

  /**
   * Collect `{sku, handle}` for every metaobject a list.metaobject_reference metafield
   * points at. `sku` is upper-cased and trimmed for comparison; `handle` is what the
   * checkout and account UI extensions key off (`_add_on_handles` / `_option_handles`).
   *
   * Returns null — NOT [] — when the metafield is absent or unreadable. The two are
   * different facts and the caller must not conflate them: [] means "this variant offers
   * nothing of that kind", null means "we do not know". Only null falls back to the old
   * undivided behaviour; [] legitimately classifies every code the other way.
   */
  function readAddOnEntries(metafield) {
    var nodes = metafield && metafield.references && metafield.references.nodes;
    if (!nodes) return null;
    var entries = [];
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      var sku = node && node.field && node.field.value;
      if (!sku) continue;
      entries.push({
        sku: String(sku).toUpperCase().trim(),
        handle: node.handle ? String(node.handle).trim() : '',
      });
    }
    return entries;
  }

  /** Parse the strike-price metafield's JSON string; null when absent or malformed. */
  function parseStrikePrice(metafield) {
    var raw = metafield && metafield.value;
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (err) {
      console.warn('[TCG Configurator] strike_price metafield was not valid JSON:', err);
      return null;
    }
  }

  /** The entry in `entries` whose sku is `code`, or null. */
  function findAddOnEntry(entries, code) {
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].sku === code) return entries[i];
    }
    return null;
  }

  // Query fragment for one add-on metafield's referenced metaobjects.
  //
  // BOTH add-on metafields are fetched so a caller can tell an add-on-type code from an
  // option-type one: the cart labels each from a DIFFERENT line-item property, and the
  // editor's sku object does not say which is which. See splitFinishingSkus.
  //
  // Both lists are needed, not just the options one. The two metaobject definitions share
  // the `BE` type — `add_on` is "Back material (BM), Mounting (MT), Frame Style (FS),
  // Background effect (BE)" and `add_on_options` is "Finishing (FN), Background effect
  // (BE), Border effect (B)" — and STIT really does carry one code, `F2`
  // (`frame-thickness-2-cm-standard`), as an `add_on` AND as an `add_on_options` entry.
  // No variant references both flavours today (checked: 0 of 387), but a merchant adding
  // the option-flavoured one to a variant that prices `F2` would move a PRICED code to
  // `_options`, where the channel-price cart transform does not look — a silent
  // undercharge. So membership in the add-on list wins; see splitFinishingSkus.
  //
  // Both metafield definitions, and both metaobject definitions behind them, are
  // `storefront: PUBLIC_READ` (verified on STIT via the Admin API — the metaobject
  // definitions matter too, or `references` comes back empty and the split no-ops), and
  // the whole query was run against the live shop: HTTP 200, no errors, ~490ms.
  //
  // `handle` rides along on nodes the connection already pays for, and it is what the
  // tcg-campaigns checkout and account UI extensions read (parseCartLineAttributes.ts).
  // Without it the cart labels a finishing choice and checkout does not — the customer
  // watches their selection disappear at the till.
  var ADD_ON_REFS =
    'references(first: 50) { nodes { ... on Metaobject { handle field(key: "sku") { value } } } }';

  function storefrontToken() {
    return global.theme && global.theme.storefront_api_token;
  }

  /**
   * The variant's strike-price JSON, in the app-reserved `cm_tier` namespace.
   *
   * Empty when the namespace cannot be built, so the field is omitted from the query
   * rather than guessed at.
   *
   * `app_scope` alone is NOT the test, and checking only it made this branch dead code.
   * html-bottom-js.liquid builds it as `app_id | prepend: 'app--' | append: '--'`, so a
   * shop with no `cm_app.app-id` metafield yields the TRUTHY string `app----` — the guard
   * never fired and the query asked for `app----cm_tier`, which resolves to nothing.
   * `app_id` is the field that is actually empty, so that is what is tested (PR #190
   * review).
   */
  function strikePriceNamespace() {
    var t = global.theme;
    if (!t || !t.app_id || !t.app_scope) return '';
    return t.app_scope + 'cm_tier';
  }

  /**
   * Candidate product handles for a SKU, from the Liquid-rendered print-material index.
   *
   * Same index the Picturator single-image path uses (`services/productLookUp`, NGE-13453),
   * and it exists for the same reason: `predictiveSearch` reads the storefront SEARCH index,
   * which by definition excludes an `UNLISTED` product — so the UC could not sell one either.
   *
   * `printMaterial` is passed by the caller when the editor supplied a structured SKU. The
   * three-letter prefix is only a fallback: every PM in the catalogue is three letters today,
   * but that is a property of the current catalogue, not a contract.
   *
   * @returns {string[]}
   */
  function configuratorHandlesFor(sku, printMaterial) {
    var index = window.theme && window.theme.configurator_pm_index;
    if (!index) return [];

    var pm = printMaterial ? String(printMaterial) : '';
    if (!pm) {
      var match = /^([A-Za-z]{3})[A-Za-z0-9]/.exec(String(sku || ''));
      if (!match) return [];
      pm = match[1];
    }
    pm = pm.toUpperCase();

    // hasOwnProperty, not a bare read: the index is JSON from a shop metafield, so a PM
    // spelled `constructor` would otherwise resolve off Object.prototype.
    if (!Object.prototype.hasOwnProperty.call(index, pm)) return [];

    var entry = index[pm];
    var list = Object.prototype.toString.call(entry) === '[object Array]' ? entry : [entry];
    var handles = [];
    for (var i = 0; i < list.length; i++) {
      if (typeof list[i] === 'string' && list[i]) handles.push(list[i]);
    }
    return handles;
  }

  /**
   * Run the variant lookup, optionally including the two add-on metafields.
   *
   * With `handles`, the query is one aliased `product(handle:)` root per candidate — a direct
   * fetch that resolves whatever the product's status is. Without them it falls back to
   * `predictiveSearch`, which is what this did exclusively before NGE-13453.
   */
  function runVariantLookup(token, sku, withAddOnMetafields, handles) {
    // GraphQL VARIABLES, never string interpolation. `sku` arrives in the editor's
    // postMessage payload, so it is attacker-influenced: concatenating it into the document
    // let a crafted value close the string and append its own selection set (CodeQL
    // "database query built from user-controlled sources"). The namespace is parameterised
    // for the same reason even though it comes from theme settings — the rule is "no
    // untrusted text in the document", not "none that I currently distrust".
    var strikeNamespace = withAddOnMetafields ? strikePriceNamespace() : '';
    var variantFields = 'id sku availableForSale';
    if (withAddOnMetafields) {
      variantFields +=
        ' addOnProducts: metafield(namespace: "add_on", key: "products") { ' +
        ADD_ON_REFS +
        ' } addOnOptions: metafield(namespace: "add_on", key: "options") { ' +
        ADD_ON_REFS +
        ' }';
      if (strikeNamespace) {
        variantFields +=
          ' original_price: metafield(namespace: $strikeNamespace, key: "strike_price") { value }';
      }
    }
    var productFields = ' { id handle variants(first: 100) { nodes { ' + variantFields + ' } } }';
    var variables = {};
    var query;

    if (handles && handles.length) {
      // Handles travel as VARIABLES like everything else here. They come from a shop
      // metafield rather than a postMessage, but the rule is "no untrusted text in the
      // document", not "none that I currently distrust" — see the note above.
      var paramList = [];
      var roots = '';
      for (var i = 0; i < handles.length; i++) {
        paramList.push('$h' + i + ': String!');
        roots += ' p' + i + ': product(handle: $h' + i + ')' + productFields;
        variables['h' + i] = handles[i];
      }
      if (strikeNamespace) {
        paramList.push('$strikeNamespace: String!');
        variables.strikeNamespace = strikeNamespace;
      }
      query = 'query VariantByHandle(' + paramList.join(', ') + ') {' + roots + ' }';
      return postLookup(token, query, variables);
    }

    var params = strikeNamespace ? '($sku: String!, $strikeNamespace: String!)' : '($sku: String!)';
    query =
      'query VariantBySku' +
      params +
      ' { predictiveSearch(query: $sku, searchableFields: [VARIANTS_SKU], limit: 5) ' +
      '{ products' +
      productFields +
      ' } }';

    variables.sku = sku;
    if (strikeNamespace) variables.strikeNamespace = strikeNamespace;
    return postLookup(token, query, variables);
  }

  /** The one place a Storefront lookup leaves the browser. */
  function postLookup(token, query, variables) {
    return fetch('/api/' + STOREFRONT_API_VERSION + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query: query, variables: variables }),
    }).then(function (r) {
      return r.json();
    });
  }

  /**
   * The variant node matching `sku` in a lookup response, or null.
   *
   * Handles BOTH shapes the query can take: the `predictiveSearch` envelope, and the aliased
   * `p0`/`p1`/… roots of the handle query. The exact SKU decides in either case, never the
   * order — a print material is not unique to one product (`ACS` is carried by both
   * `foto-su-acrilico` and `sottobicchieri-personalizzati` on stit-prod).
   */
  function pickVariantNode(data, sku) {
    var root = data && data.data;
    if (!root) return null;

    var products;
    if (root.predictiveSearch) {
      products = root.predictiveSearch.products;
    } else {
      // Aliased handle roots. A handle that resolves to nothing comes back as null.
      products = [];
      for (var key in root) {
        if (Object.prototype.hasOwnProperty.call(root, key) && root[key]) products.push(root[key]);
      }
    }
    if (!products) return null;
    for (var i = 0; i < products.length; i++) {
      var variants = products[i].variants && products[i].variants.nodes;
      if (!variants) continue;
      for (var j = 0; j < variants.length; j++) {
        if (variants[j].sku === sku) return variants[j];
      }
    }
    return null;
  }

  /**
   * Just the add-on/option lists for a SKU — used to enrich a variant whose id came from
   * the static skuVariantMap, which carries no metafields of its own.
   *
   * A non-null return means the metafield SELECTION ran and this variant's node came
   * back, so both lists are authoritative (`null` inside one of them then means "this
   * variant references none of that kind", not "unknown"). `null` means the lookup itself
   * did not happen or found nothing — see isDegraded.
   *
   * @returns {Promise<{addOnEntries: Array|null, optionEntries: Array|null,
   *          strikePrice: Object|null}|null>}
   */
  function fetchAddOnEntries(cfg, sku, handles) {
    var token = storefrontToken();
    if (!token) return Promise.resolve(null);
    return runVariantLookup(token, sku, true, handles)
      .then(function (data) {
        // A stale index misses a product created or renamed after the last `build:sku-index`
        // run. Search still finds a listed one, which is where this lookup went before
        // NGE-13453 — without the retry a pinned variant lost its add-on split and its strike
        // price whenever the index lagged the catalogue. NGE-13492.
        //
        // Only when the handle query actually RAN (`data.data`). A query rejected outright —
        // the shop denies the add-on metafields — would be rejected again by the identical
        // selection on the search path, so retrying costs a request, finds nothing, and the
        // log below would report the second rejection instead of the first.
        var ran = data && data.data;
        if (ran && !pickVariantNode(data, sku) && handles && handles.length) {
          return runVariantLookup(token, sku, true, []);
        }
        return data;
      })
      .then(function (data) {
        if (data && data.errors) {
          console.error('[TCG Configurator] Add-on lookup reported errors:', data.errors);
        }
        var node = pickVariantNode(data, sku);
        if (!node) return null;
        // Return whatever was readable, each field on its own merits. Gating the whole
        // object on `optionEntries` threw away a strike price that had parsed fine, so a
        // static-map variant with no options metafield lost its savings at checkout for
        // data that was in hand — two independent facts coupled by one gate (PR #190
        // review). The caller decides what a missing field means.
        return {
          addOnEntries: readAddOnEntries(node.addOnProducts),
          optionEntries: readAddOnEntries(node.addOnOptions),
          strikePrice: parseStrikePrice(node.original_price),
          // Same partial-failure rule as the dynamic path: `data` alongside `errors` can
          // null one metafield while the other resolves, and a null list must not then be
          // read as "references none of that kind".
          classified: !(data && data.errors),
        };
      })
      .catch(function (err) {
        console.warn('[TCG Configurator] Add-on lookup failed:', err);
        return null;
      });
  }

  /**
   * Look up Shopify variant by SKU via Storefront API predictiveSearch.
   * Matches the pattern from picturator-configurator.js / productLookUp service.
   * Falls back to static skuVariantMap from section settings.
   *
   * The resolved variant carries `addOnEntries` / `optionEntries` — the finishing codes
   * this variant offers, split by which metaobject list they live in. On the static-map
   * path the id comes from the map and those lists are fetched separately, so the
   * classification still works there; both are null only when the lookup could not read
   * them at all, which makes splitFinishingSkus fall back to its pre-NGE-11736 behaviour.
   */
  /**
   * Is this resolved variant missing its classification?
   *
   * The test is `classified`, a flag the resolution paths set when the add-on metafield
   * SELECTION actually ran and returned a node. It is deliberately not `!optionEntries`:
   * a variant that simply has no `add_on.options` metafield reads back as null there, and
   * treating that as a failure meant such a SKU was never cached (a fresh Storefront
   * round-trip on every add-to-cart for the page life) and, on the static-map path, drew a
   * `console.error` asserting its lists "could not be fetched" when they had been fetched
   * perfectly — a false error in a shop wired to Instana EUM (PR #190 review).
   *
   * A degraded result is still USABLE — the line item is correct, just unlabelled — but it
   * must never be cached, or one transient failure freezes the pre-fix behaviour for that
   * SKU for the rest of the page life with nothing to retry it.
   */
  function isDegraded(variant) {
    return !variant || !variant.classified;
  }

  function findVariantBySku(cfg, sku, printMaterial) {
    if (!sku) return Promise.resolve(null);

    // Resolved once and threaded through every path below, so the static-map enrichment and
    // the dynamic lookup agree on which products they are asking about.
    var handles = configuratorHandlesFor(sku, printMaterial);

    // The cache holds PROMISES, not results. Two add-to-carts for the same SKU in flight at
    // once — a double-click, or two postMessages — used to both miss the cache and race:
    // the second could read a half-built entry whose classification had not arrived yet and
    // silently take the degraded path, putting two lines in one cart where only one is
    // labelled and priced (PR #190 review). Caching the promise makes the second caller
    // await the first. A promise that resolves degraded is evicted below, so a transient
    // failure is retried rather than remembered.
    if (variantCache.has(sku)) return variantCache.get(sku);

    // Check static map (from section settings linked products).
    //
    // The map carries an id and nothing else, so on its own this path cannot classify
    // finishing codes and every one of them would stay in `_add_ons` — the pre-NGE-11736
    // behaviour, silently, on a shop where the fix looks deployed. So the id still comes
    // from the map (it is the merchant's explicit choice of variant, and MUST keep
    // winning), but the add-on lists are fetched alongside it.
    //
    // Deliberately not reordered to "Storefront first, map as fallback": the map exists so
    // a merchant can pin which variant a SKU resolves to, and letting a search result
    // override that would change which product is bought, not just how it is labelled.
    if (cfg._skuVariantMap && cfg._skuVariantMap[sku]) {
      var staticResult = { id: String(cfg._skuVariantMap[sku]), sku: sku };
      var staticPending = fetchAddOnEntries(cfg, sku, handles)
        .then(function (entries) {
          if (entries) {
            // Field by field: a readable strike price is worth keeping even when the
            // classification is not available.
            staticResult.addOnEntries = entries.addOnEntries;
            staticResult.optionEntries = entries.optionEntries;
            staticResult.strikePrice = entries.strikePrice;
            // The lookup ran and returned this variant's node, so both lists are answers —
            // a null one means "references none of that kind". Only the absence of the
            // whole object, or a partial GraphQL failure, is not.
            staticResult.classified = entries.classified;
          }
          if (isDegraded(staticResult)) {
            // Never remember a result we could not classify — see isDegraded.
            variantCache.delete(sku);
            // Not fatal — the line item is still correct, just unlabelled in the cart.
            // Loud because the symptom (a missing option) gives no hint that a section
            // setting plus a failed lookup decided it.
            console.error(
              '[TCG Configurator] Variant came from the static skuVariantMap and its add-on ' +
                'lists could not be fetched, so every finishing code will be sent as an ' +
                'add-on and options will not be labelled in the cart:',
              sku
            );
          }
          return staticResult;
        })
        .catch(function () {
          variantCache.delete(sku);
          return staticResult;
        });
      variantCache.set(sku, staticPending);
      return staticPending;
    }

    // Dynamic lookup via Storefront API
    var token = storefrontToken();
    if (!token) {
      console.warn(
        '[TCG Configurator] No storefront_api_token — cannot look up variant for SKU:',
        sku
      );
      return Promise.resolve(null);
    }

    function runLookup(withAddOnMetafields, lookupHandles) {
      return runVariantLookup(token, sku, withAddOnMetafields, lookupHandles);
    }

    // One attempt WITH the add-on metafields, retried without them when the query is rejected
    // outright. Both the handle query and the search fallback go through this: the fallback
    // used to ask only with the metafields, so on a shop that rejects that selection a stale
    // index turned an add-to-cart that used to succeed into "No Shopify variant". NGE-13492.
    //
    // `startLean`: the handle query was already rejected WITH the metafields, so the search
    // fallback skips the full attempt the shop will reject again — two requests saved per add.
    function lookupWithLeanRetry(lookupHandles, startLean) {
      if (startLean) {
        return runLookup(false, lookupHandles).then(function (lean) {
          return { data: lean, classified: false, rejected: true };
        });
      }
      return runLookup(true, lookupHandles).then(function (data) {
        // `classified` travels WITH the response instead of being inferred from it later.
        // Whether the two add-on lists are answers or guesses is a fact about the QUERY —
        // did the selection run, and did it run cleanly — and only this step knows it.
        // Re-deriving it downstream from `optionEntries === null` conflated "this variant
        // references no options" with "we never asked" (PR #190 review).
        if (data && data.errors) {
          console.error('[TCG Configurator] Storefront lookup reported errors:', data.errors);
        }
        // A PARTIAL failure counts as unclassified. GraphQL can return `data` alongside
        // `errors` with the offending field nulled — one metafield denied while the other
        // resolves. A null list would then read as "references none of that kind" and a
        // priced code could be classified as a free option. Errors present ⇒ do not trust
        // the lists, even though the id and availability are still good.
        if (data && data.data) return { data: data, classified: !data.errors };

        // No `data` at all means the QUERY was rejected, not that the variant is missing —
        // a validation error on the metafield selection, an access denial, or throttling
        // (this query asks for far more nodes than the pre-NGE-11736 one did). The add-on
        // metafields are the only thing that selection added, so retry without them:
        // losing the classification degrades the cart label, whereas returning null here
        // would make the caller throw and block EVERY configurator add-to-cart.
        console.error(
          '[TCG Configurator] Variant lookup rejected with the add-on metafields; ' +
            'retrying without them (classification will be unavailable) for SKU:',
          sku
        );
        return runLookup(false, lookupHandles).then(function (lean) {
          return { data: lean, classified: false, rejected: true };
        });
      });
    }

    var pending = lookupWithLeanRetry(handles)
      .then(function (answer) {
        var node = pickVariantNode(answer && answer.data, sku);

        // A handle query that found nothing means the index is STALE for this SKU — the
        // product was created, or renamed, after the last `build:sku-index` run. Retry
        // through the search index rather than failing: for a listed product that still
        // works, so a stale index degrades to the pre-NGE-13453 behaviour instead of
        // breaking an add-to-cart that used to succeed. It cannot rescue an UNLISTED
        // product — nothing can, until the index is regenerated — but it must not make
        // the common case worse than it was.
        if (!node && handles.length) {
          return lookupWithLeanRetry([], answer && answer.rejected).then(function (searched) {
            var searchedNode = pickVariantNode(searched && searched.data, sku);
            if (!searchedNode) return null;
            return { node: searchedNode, classified: searched.classified };
          });
        }

        if (!node) return null;
        return { node: node, classified: answer.classified };
      })
      .then(function (picked) {
        if (!picked) return null;
        var node = picked.node;
        var result = {
          id: node.id.split('/').pop(),
          sku: sku,
          gid: node.id,
          availableForSale: node.availableForSale,
          addOnEntries: readAddOnEntries(node.addOnProducts),
          optionEntries: readAddOnEntries(node.addOnOptions),
          strikePrice: parseStrikePrice(node.original_price),
          // `picked`, not the outer `answer` — the search retry above produces its own
          // answer, and reading the handle query's flag there would label a retried
          // variant with the classification of a response it never came from.
          classified: picked.classified,
        };
        return result;
      })
      .catch(function (err) {
        console.warn('[TCG Configurator] Storefront API lookup failed:', err);
        return null;
      });

    // See the note at the cache read: the PROMISE is cached so concurrent adds for the same
    // SKU share one lookup, and a resolution we could not classify is evicted so the next
    // add retries instead of inheriting it. A shop that genuinely does not expose the
    // metafields pays one extra request per add — two when the index is stale and the add
    // falls back to search (handle full, handle lean, search lean) — bounded and logged.
    variantCache.set(sku, pending);
    return pending.then(function (result) {
      if (isDegraded(result)) variantCache.delete(sku);
      return result;
    });
  }

  // =========================================================================
  // SHOPIFY CART
  // =========================================================================

  /**
   * Derive the selected finishing SKU codes from a structured sku object.
   * PM and F are the base product/format codes — everything else that is non-null
   * is a selection the customer made. Per the tcg-campaigns metaobject definitions:
   * FS=frame style, MT=mounting, BM=back material, BE=background effect (the one type
   * that appears in BOTH definitions), FN=finishing, B=border effect.
   * Returns an array of selected SKU codes, or an empty array.
   *
   * Note: _add_on_handles and _add_ons_display need each metaobject's `handle` and
   * `display_title`, which findVariantBySku does not fetch — it reads only `sku`, the
   * one field the split below needs. Those properties therefore stay omitted.
   * See: src/assets/js/services/addToCartData/index.js for the full implementation.
   */
  function deriveFinishingSkus(skuObj) {
    if (!skuObj) return [];
    var BASE_KEYS = { _: true, PM: true, F: true };
    var parts = [];
    for (var key in skuObj) {
      if (!Object.prototype.hasOwnProperty.call(skuObj, key)) continue;
      if (BASE_KEYS[key]) continue;
      if (skuObj[key]) parts.push(skuObj[key]);
    }
    return parts;
  }

  /**
   * The price-map key a SECOND Picturator product's prices live under, on a variant that two
   * products share.
   *
   * Picturator keys pricing by (productId, sku), so a SKU is unique only within a product: on
   * STIT `Global_Forex` (4388) and `Global_Design_Forex` (4658) both emit FXW0800601. One of
   * them owns the Shopify variant and is `_`; the other is published under `P4658`, by
   * NGE-12745, into the same `mg_price_N` and `strike_price` maps this file already reads.
   *
   * **Digits only, upper case.** This is the third implementation of one rule — the others are
   * `productPriceKey.ts` in tcg-campaigns, which WRITES the key, and the two Rust cart
   * Functions, which read it. They have to agree exactly: a key built even slightly
   * differently is simply absent from the map, and the strike then falls back to the owner's
   * with nothing to show for it. The digit guard is there for the same reason it is there —
   * `productId` arrives in a postMessage payload, so it is attacker-influenced, and a
   * `P`-prefixed add-on code (`PC` is a real one) would otherwise select an add-on's surcharge
   * as the entire strike.
   *
   * @param {string|number|null|undefined} productId
   * @param {string} [addOnCode] - scopes the key to one add-on, e.g. `P4658:DB`
   * @returns {string|null} null when the id is not one the readers would resolve
   */
  function productScopedPriceKey(productId, addOnCode) {
    if (productId === null || productId === undefined) return null;
    var id = String(productId).trim();
    if (!/^[0-9]+$/.test(id)) return null;
    if (addOnCode === undefined || addOnCode === null) return 'P' + id;
    // An EMPTY code must not fall through to the base key. `'' ? a : b` picks b, so the
    // obvious ternary returns 'P4658' for a blank code and the caller then adds the whole
    // scoped base price a second time. run.rs guards the same case with an explicit
    // `if sku_trimmed.is_empty() { continue; }`.
    var code = String(addOnCode).trim().toUpperCase();
    if (!code) return null;
    return 'P' + id + ':' + code;
  }

  /**
   * `_original_price_per_unit` — the pre-discount price per market group, as
   * `[{mg, p}, …]` in minor units.
   *
   * WHY IT MUST BE EMITTED, even though the cart looks right without it. The CART has a
   * Liquid fallback: `line-item-price.liquid` uses this property when present and otherwise
   * recomputes the strike from the variant's own `cm_tier.strike_price`. CHECKOUT and the
   * ACCOUNT order view have NO fallback — `checkout-original-price` and
   * `account-original-price` in tcg-campaigns read this property and nothing else
   * (useOriginalPrice.ts, useTotalOriginalPrice.ts). Without it a UC line shows its savings
   * in the cart and then silently loses them at checkout: the same split-brain the add-on
   * handles fixed, one step further down the funnel.
   *
   * Mirrors addToCartData's #calculateOriginalPricePerUnit deliberately. Both paths feed the
   * SAME extension, so a different shape or rounding would make a UC line and a canvas line
   * disagree inside one checkout.
   *
   * Add-on strikes are added for `_add_ons` codes ONLY. Option-type codes are free and are
   * not keys in the strike JSON at all (verified on STIT CVS0800601, whose map holds
   * F2/F4/HC/S* and no border effects), so adding them could only reintroduce the
   * classification confusion this ticket removes.
   *
   * @param {Object|null} priceData - parsed cm_tier.strike_price JSON
   * @param {string[]} addOnCodes - the codes that went to `_add_ons`
   * @param {string|number|null} [productId] - the Picturator product the line was configured
   *   with, when the payload carries one. NGE-12871.
   * @param {string|number|null} [pageCount] - the photobook's page count (`_tcg_page_count`).
   *   Adds the same page surcharge `channel-price` charges, so the saving stays constant
   *   however many pages the book has. NGE-14089.
   * @returns {Array<{mg: string, p: number}>} empty when it cannot be computed
   */
  function computeOriginalPricePerUnit(priceData, addOnCodes, productId, pageCount) {
    var out = [];
    var index = global.theme && global.theme.market_group && global.theme.market_group.index;
    if (!index || !priceData) return out;

    // A line configured in a design editor names the Picturator product it was made with, and
    // that product's strike is NOT the variant's. An 80x60 star map struck through at the
    // photo panel's 95,00 instead of its own 80,00 — a savings badge is a price claim, and it
    // was overstated on every star-map line. NGE-12871.
    //
    // The scoped entry SUBSTITUTES the base; it never adds to it. And the decision is made
    // PER MARKET GROUP, not once for the whole map: getStrikePriceMetafieldValue emits a key
    // per market group and drops any group whose matrix has no numeric Base, so
    // `{_: {EU, CH}, P4658: {EU}}` is an ordinary shape rather than a corrupt one. Choosing
    // once for the map gave the CH customer `P4658['CH']` — undefined, i.e. a 0,00 original
    // price rendered against a real charge.
    var scopedBaseKey = productScopedPriceKey(productId);
    var scopedBase = scopedBaseKey ? priceData[scopedBaseKey] : null;

    // One value for every market group, like the charge in channel-price. NGE-14089.
    var pageSurcharge = computePageSurcharge(priceData._pages, pageCount);

    for (var group in index) {
      if (!Object.prototype.hasOwnProperty.call(index, group)) continue;
      // `_v` is a schema version and `_p` the primary-market pointer, not market groups.
      if (group === '_v' || group === '_p') continue;

      var mg = String(group).toUpperCase();
      var lower = mg.toLowerCase();

      var scopedPrice = scopedBase ? scopedBase[mg] : undefined;
      if (scopedPrice === undefined && scopedBase) scopedPrice = scopedBase[lower];

      // The whole scoped path is on only where the scoped BASE resolved for this group —
      // the same gate both Rust readers apply, where `product_key` is filtered on
      // `price_data.get("P<id>").is_some()` before any add-on key is built. Without it the
      // theme could take the owner's base and the product's add-on, a combination no reader
      // produces, and strike a number that is neither.
      var useScoped = scopedPrice !== undefined && scopedPrice !== null;

      var basePrice;
      if (useScoped) {
        basePrice = scopedPrice;
      } else {
        var ownBase = priceData._;
        var ownPrice = ownBase === undefined || ownBase === null ? undefined : ownBase[mg];
        if (ownPrice === undefined && ownBase) ownPrice = ownBase[lower];

        // A group neither product prices is OMITTED, not struck at zero. This also corrects
        // the pre-existing `|| 0`, which emitted `{ mg, p: 0 }` for a market group missing
        // from `_`: the cart hides a strike below the current price, but checkout and the
        // account view render on `originalPrice > 0`... and 0 is not > 0, so they showed
        // nothing while the cart showed nothing — consistent, but only by accident. A real
        // authored 0 is distinguishable from absent here and is still emitted.
        if (ownPrice === undefined || ownPrice === null) continue;
        basePrice = ownPrice;
      }

      var price = basePrice;

      for (var i = 0; i < (addOnCodes || []).length; i++) {
        // Key lookup is case-insensitive. The UC path normalises codes to upper case while
        // addToCartData passes the metaobject's `sku` verbatim, so a strike map keyed in
        // lower case resolved on one path and not the other — the two paths feed the SAME
        // checkout extension, so that divergence puts two different strikes in one order
        // summary (PR #190 review).
        var code = addOnCodes[i];
        if (code === undefined || code === null || String(code).trim() === '') continue;

        // The product's own add-on strike wins over the owner's, for the same reason the
        // base does. Present-but-unreadable contributes 0 rather than falling back to the
        // owner's surcharge: `or_else` in run.rs fires only when the key is ABSENT, so
        // falling back here would strike a number the Function never charges.
        var scopedAddonKey = useScoped ? productScopedPriceKey(productId, code) : null;
        if (scopedAddonKey && Object.prototype.hasOwnProperty.call(priceData, scopedAddonKey)) {
          var scopedEntry = priceData[scopedAddonKey];
          price += (scopedEntry && (scopedEntry[mg] || scopedEntry[lower])) || 0;
          continue;
        }

        var entry =
          priceData[code] ||
          priceData[String(code).toUpperCase()] ||
          priceData[String(code).toLowerCase()];
        if (entry) price += entry[mg] || entry[lower] || 0;
      }

      price += pageSurcharge;

      out.push({ mg: mg, p: price });
    }
    return out;
  }

  /**
   * Page surcharge in minor units for the strike price, from the `_pages` entry
   * tcg-campaigns publishes in `strike_price`: `max(0, pages − basePages) / pageStep ×
   * pricePerStep`. The same formula and the same guards as `page_surcharge` in
   * channel-price (run.rs) — a strike that grows differently from the charge would
   * misstate the saving. Without `_pages`, or with a page count that is not 1–1000 in
   * digits, it is 0 and the strike is what it was before. NGE-14089.
   *
   * @param {Object|null} pagePricing - `{ basePages, pageStep, pricePerStep }`
   * @param {string|number|null} pageCount
   * @returns {number}
   */
  function computePageSurcharge(pagePricing, pageCount) {
    if (!pagePricing || typeof pagePricing !== 'object') return 0;
    var base = pagePricing.basePages;
    var step = pagePricing.pageStep;
    var perStep = pagePricing.pricePerStep;
    if (typeof base !== 'number' || typeof step !== 'number' || typeof perStep !== 'number')
      return 0;
    if (base < 0 || step <= 0 || perStep < 0) return 0;

    var raw = pageCount === undefined || pageCount === null ? '' : String(pageCount).trim();
    if (!/^[0-9]{1,4}$/.test(raw)) return 0;
    var pages = parseInt(raw, 10);
    if (pages < 1 || pages > 1000) return 0;

    return Math.round((Math.max(0, pages - base) / step) * perStep);
  }

  /**
   * Split derived finishing codes across the two line-item properties the cart reads.
   *
   * `line-item-add-ons.liquid` does not scan one bag of codes: it walks the variant's
   * `add_on.products` metaobjects and matches those against `_add_ons`, then walks its
   * `add_on.options` metaobjects and matches those against `_options`. A code in the
   * wrong property matches neither list and the selection is dropped from the cart
   * silently — which is why every option-type choice out of the UC was invisible while
   * this function put all of them in `_add_ons`. NGE-11736.
   *
   * Precedence is deliberate and money-shaped:
   *
   *   1. in the variant's ADD-ON list  → `_add_ons`   (priced wins, even if also an option)
   *   2. else in its OPTION list       → `_options`
   *   3. in neither                    → `_add_ons`
   *
   * Rules 1 and 3 both protect a surcharge. `channel-price` in tcg-campaigns prices from
   * `_add_ons` alone (and `volume-discount` gates its `ao` list on the same property), so
   * a priced code that lands in `_options` is silently free. Over-inclusion in `_add_ons`
   * cannot cost anything the other way — the transform only charges for a code the
   * variant's tier-price JSON actually keys. Rule 1 is not hypothetical: the two metaobject
   * definitions share the `BE` type, and STIT carries `F2` as both an `add_on` and an
   * `add_on_options` entry.
   *
   * Codes are emitted UPPER-CASED AND TRIMMED — the same normalisation this function
   * matches on. Liquid compares with `| upcase` but never strips, so returning the raw
   * code could put a value in `_options` that the cart then fails to match, rendering it
   * nowhere while it is also absent from `_add_ons`.
   *
   * Handles ride along for the codes that resolved to a metaobject, because the checkout
   * and account UI extensions key off `_add_on_handles` / `_option_handles` rather than the
   * skus. A code in NEITHER list has no metaobject and therefore no handle, so the handle
   * lists can be shorter than the code lists — that is honest rather than lossy: before
   * this, UC line items carried no handles at all and checkout labelled nothing.
   *
   * @param {string[]} skus - derived finishing codes
   * @param {Object|null} variant - resolved variant from findVariantBySku
   * @returns {{addOns: string, addOnHandles: string, options: string, optionHandles: string,
   *          addOnCodes: string[]}} the four strings are comma-separated and any may be
   *          empty; `addOnCodes` is `addOns` unjoined, for the strike calculation
   */
  function splitFinishingSkus(skus, variant) {
    // Normalise AND de-duplicate. The editor can legitimately carry one code under two sku
    // keys — `BE` is a valid type in both metaobject definitions, which is the whole reason
    // this function exists — and a repeated code would be priced twice: `channel-price` in
    // tcg-campaigns sums every entry of `_add_ons`, and line-item-price.liquid iterates it
    // the same way, so `F2,F2` charges the surcharge twice for one selection. The React
    // widget cannot hit this because it filters the variant's metaobject list; this path
    // builds the list itself, so it must dedupe explicitly (PR #190 review).
    var normalised = [];
    for (var n = 0; n < skus.length; n++) {
      var code = String(skus[n]).toUpperCase().trim();
      if (code && normalised.indexOf(code) === -1) normalised.push(code);
    }

    // Whether a code MAY be classified as an option is one question with one answer:
    // `variant.classified`, set by the resolution path that knows whether the metafield
    // selection ran and ran cleanly. Rule 1 gives the priced add-on list precedence, so it
    // cannot be applied without BOTH lists — reading the option list alone, and coercing a
    // null add-on list to `[]`, would let a partial GraphQL failure (one metafield denied,
    // the other resolved) put a PRICED code in `_options`, where `channel-price` never
    // looks: silently free. Unclassified therefore means undivided — everything to
    // `_add_ons`, the pre-NGE-11736 shape (PR #190 review).
    //
    // Once classified, a null list is a real answer ("references none of that kind") and is
    // read as empty. The handles of a populated ADD-ON list are still emitted either way,
    // or checkout labels nothing — the symptom this ticket exists to remove.
    var classified = !!(variant && variant.classified);
    var optionEntries = (variant && variant.optionEntries) || [];
    var addOnEntries = (variant && variant.addOnEntries) || [];
    var addOns = [];
    var addOnHandles = [];
    var options = [];
    var optionHandles = [];
    for (var i = 0; i < normalised.length; i++) {
      var addOnHit = findAddOnEntry(addOnEntries, normalised[i]);
      var optionHit = addOnHit || !classified ? null : findAddOnEntry(optionEntries, normalised[i]);
      if (optionHit) {
        options.push(normalised[i]);
        if (optionHit.handle) optionHandles.push(optionHit.handle);
      } else {
        addOns.push(normalised[i]);
        if (addOnHit && addOnHit.handle) addOnHandles.push(addOnHit.handle);
      }
    }
    return {
      addOns: addOns.join(','),
      addOnHandles: addOnHandles.join(','),
      options: options.join(','),
      optionHandles: optionHandles.join(','),
      // The same codes as `addOns`, unjoined, for the strike calculation. It MUST be this
      // list and not a subset — see the call site.
      addOnCodes: addOns,
    };
  }

  // =========================================================================
  // PICTURATOR ORDER-IMPORT CONTRACT (NGE-10195; importer side NGE-10122)
  // =========================================================================
  //
  // Picturator imports every Shopify line item down one of two paths and cannot tell
  // them apart on its own, so each line item declares its path in `_tcg_source`:
  //
  //   single-configurator     the design lives in Picturator's `configureditems`
  //                           table. The importer resolves it from `_configuration_id`
  //                           (a UUID) and rebuilds product, images and SKU from that
  //                           row. Canvas uploads and the design editors (starmap,
  //                           citymap, typo) work this way — the UC creates the
  //                           Picturator item and hands back its id as `picanovaItemId`.
  //
  //   universal-configurator  the print data lives in the UC service (multi-page
  //                           products: photobook, calendar, photocard). The importer
  //                           resolves the product from `_sku` and pulls the print PDF
  //                           via `_tcg_config_id`. Such a line item usually ALSO carries
  //                           a UUID `_configuration_id` — verified on a live photobook,
  //                           2026-08-11 — because the UC saves a Picanova configuration
  //                           for these products too. Both ids travel; `_tcg_source` says
  //                           which one the importer should key on.
  //
  // `_configuration_id` used to fall back to `payload.configurationId`. Every payload
  // without a `picanovaItemId` therefore wrote the UC token into the Picturator field,
  // and the importer rejected the ENTIRE order, not just that line. The two id formats do
  // not collide (UUID vs short token such as `Lb0vuCbuwV_f`), which is what lets the code
  // below tell a real id from a cross-wired one instead of hoping a test order catches it.

  var SOURCE_SINGLE = 'single-configurator';
  var SOURCE_UC = 'universal-configurator';
  var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  // Editor → import path. This map decides; `payload.source` is only consulted for an
  // editor that is NOT listed here (see resolveImportSource for why).
  var UC_EDITORS = { photobook: true, calendar: true, photocard: true, 'multi-image': true };
  var SINGLE_EDITORS = { 'single-upload': true };

  /**
   * Read a contract field from the add-to-cart payload.
   *
   * The Universal Configurator is one embed hosting SEVERAL configurators — design,
   * photobook, calendar, multi-image and more. That is the point of the name: some of
   * those products are multi-page, others are single-image, and they do not describe
   * themselves identically.
   *
   *   Single-image / design configurators post their fields FLAT
   *   (`configurationId`, `picanovaItemId`, `sku`, `editorType`, …).
   *
   *   The multi-page ones put the editor descriptor under `payload` and the product
   *   facts under `configuration`:
   *   `{ quantity, configuration: { productId, productSku, pageCount },
   *      payload: { source, editorType, configurationId, … } }` (NGE-10122).
   *
   * Reading through all four spellings is what keeps a photobook from arriving without
   * the fields the importer needs. `configuration` never repeats a key from the other
   * levels, so the lookup order cannot shadow anything. A falsy value reads as absent,
   * which is correct for every field here — none of them has a meaningful `0` or `""`.
   */
  function readPayloadField(payload, key) {
    if (payload[key]) return payload[key];
    var nested = payload.payload;
    if (nested && typeof nested === 'object' && nested[key]) return nested[key];
    if (payload.configuration && payload.configuration[key]) return payload.configuration[key];
    if (nested && nested.configuration && nested.configuration[key]) {
      return nested.configuration[key];
    }
    return '';
  }

  /**
   * Decide which import path a line item belongs to.
   *
   * The editor type decides FIRST, and `payload.source` is only a hint for editors we
   * have not mapped. That order is deliberate: the UC stamps
   * `source: "universal-configurator"` as a constant meaning "this came out of the
   * Universal Configurator" — its single-upload editor sends exactly that while being a
   * single-image editor (see tcg-configurator
   * src/editors/single-upload/services/checkoutService.ts). Trusting `source` first would
   * therefore route single-image products down the multi-page import path. The editor
   * type is what actually correlates with Picturator's product type.
   *
   * Every step below the map logs, so a line item classified on a hint or on evidence
   * cannot pass unnoticed.
   */
  function resolveImportSource(payload, editorType) {
    var editor = editorType || '';
    if (UC_EDITORS[editor]) return SOURCE_UC;
    if (SINGLE_EDITORS[editor] || editor.indexOf('design-') === 0) return SOURCE_SINGLE;

    var declared = (readPayloadField(payload, 'source') || '').trim();
    if (declared === SOURCE_SINGLE || declared === SOURCE_UC) {
      console.error(
        '[TCG Configurator] Editor "' +
          editor +
          '" is unmapped — falling back to the payload hint "' +
          declared +
          '". Add it to UC_EDITORS / SINGLE_EDITORS.'
      );
      return declared;
    }

    // No map entry and no usable hint: go by the evidence. A Picanova item id can only
    // exist because the UC saved a Picanova configuration, which is the single path.
    var inferred = readPayloadField(payload, 'picanovaItemId') ? SOURCE_SINGLE : SOURCE_UC;
    console.error(
      '[TCG Configurator] Editor "' +
        editor +
        '" is unmapped and the payload declares no usable source (' +
        (declared || 'none') +
        ') — inferred ' +
        inferred +
        '. Add it to UC_EDITORS / SINGLE_EDITORS.'
    );
    return inferred;
  }

  /**
   * Returns null when the line item satisfies the import contract, otherwise the
   * reason it does not.
   *
   * Blocking the cart add is deliberate: without a Picanova item id there is no
   * configuration in Picturator at all, so the order would be paid and unfulfillable —
   * strictly worse than an add-to-cart the customer can retry. Same call as the
   * Picturator upload path already makes (see picturator-configurator #onAddToCart).
   */
  function contractViolation(source, ids) {
    if (source === SOURCE_SINGLE) {
      return ids.configurationId
        ? null
        : 'single-configurator line item without a Picturator configuration id';
    }
    if (!ids.tcgConfigId) {
      return 'universal-configurator line item without a UC configuration id';
    }
    if (!ids.sku) {
      return 'universal-configurator line item without a product SKU';
    }
    return null;
  }

  /**
   * Add item to Shopify cart via /cart/add.js
   * Uses Storefront API to resolve SKU → variant ID (same as picturator-configurator).
   */
  function addToShopifyCart(cfg, payload) {
    // Prefer structured sku._ for variant lookup; fall back to productCode string.
    // `configuration.productSku` is the third accepted spelling: the multi-page editors
    // (photobook, calendar) describe their product in a `configuration` object rather
    // than a typed `sku` one. `_sku` is contract-relevant for those — it is how the
    // Picturator importer resolves a UNIVERSAL_CONFIGURATOR product — so an empty value
    // here would block the add rather than silently ship an unimportable line item.
    var structuredSku = payload.sku && payload.sku._ ? payload.sku : null;
    var sku = structuredSku
      ? structuredSku._
      : payload.productCode || (payload.configuration && payload.configuration.productSku) || '';

    // A Shopify variant SKU is PM + F and nothing else. The editor's `_` is the FULL
    // configuration string and carries the finishing segments too — frame, mounting,
    // border, backing. Looking the variant up by `_` therefore fails for every product
    // that has finishing options: the design editor returned CVS0800601F4HCSB where the
    // variant is CVS0800601, and the add silently died as "No Shopify variant for SKU".
    // Photobooks never hit it because their SKUs carry no finishing segments.
    //
    // The integration guide says the same thing: resolve the variant from the typed
    // sku.{PM,F,…} fields, and carry the rest as line-item properties — which the
    // properties block below already does via _sku, _PM, _F and _add_ons.
    var lookupSku = sku;
    if (structuredSku && structuredSku.PM && structuredSku.F) {
      lookupSku = structuredSku.PM + structuredSku.F;
    }

    // Import contract — resolved before the variant lookup so a doomed add costs no
    // Storefront API call. See the PICTURATOR ORDER-IMPORT CONTRACT block above.
    var editorType = readPayloadField(payload, 'editorType') || cfg.editorType || '';
    var importSource = resolveImportSource(payload, editorType);

    var ucConfigId = readPayloadField(payload, 'configurationId');

    // `_configuration_id` carries Picanova's configured-item id and nothing else. The UC
    // takes it from the Picanova configuration-save response, so it is not ours to
    // reformat — it is dropped only when it is provably the wrong value, i.e. the UC's own
    // project id. That, and not a malformed id, was the actual defect: the old code read
    // `picanovaItemId || configurationId`, so every payload without a Picanova id wrote the
    // UC token into the field the importer keys on.
    //
    // The UUID shape is checked but NOT enforced: it is what Picanova returns today
    // (`configurationApiResponse.itemId`), yet dropping an unexpected-but-real id would
    // turn a working add-to-cart into a blocked one. Beacon instead, and let it through.
    var picanovaItemId = readPayloadField(payload, 'picanovaItemId');
    if (picanovaItemId && ucConfigId && picanovaItemId === ucConfigId) {
      console.error(
        '[TCG Configurator] picanovaItemId equals the UC configurationId — dropping it, ' +
          'the Picanova configuration was never saved:',
        picanovaItemId
      );
      picanovaItemId = '';
    } else if (picanovaItemId && !UUID_RE.test(picanovaItemId)) {
      console.error(
        '[TCG Configurator] picanovaItemId is not UUID-shaped, passing it through anyway:',
        picanovaItemId
      );
    }

    var violation = contractViolation(importSource, {
      configurationId: picanovaItemId,
      tcgConfigId: ucConfigId,
      sku: sku,
    });
    if (violation) {
      console.error('[TCG Configurator] Cart add blocked — ' + violation, {
        editor: editorType,
        source: importSource,
        sku: sku,
        configuration_id: picanovaItemId,
        tcg_config_id: ucConfigId,
      });
      return Promise.reject(
        new Error(
          (global.toast_translations &&
            global.toast_translations.cart &&
            global.toast_translations.cart.configuration_failed) ||
            'Something went wrong with your configuration. Please try again.'
        )
      );
    }

    return findVariantBySku(cfg, lookupSku, structuredSku && structuredSku.PM).then(
      function (variant) {
        if (!variant || !variant.id) {
          console.error(
            '[TCG Configurator] No Shopify variant found for SKU:',
            lookupSku,
            '(full configuration SKU:',
            sku + ')'
          );
          throw new Error('No Shopify variant for SKU: ' + lookupSku);
        }

        // Format thumbnail — strip data: URLs (too large for cart properties)
        // and remove ?size= params (matching picturator-configurator pattern)
        var thumbnail = payload.previewUrl || '';
        if (thumbnail.indexOf('data:') === 0) {
          thumbnail = '';
        } else if (thumbnail) {
          try {
            var thumbUrl = new URL(thumbnail);
            thumbUrl.searchParams.delete('size');
            thumbnail = thumbUrl.toString();
          } catch (_) {}
        }

        // Build line item properties matching picturator-configurator pattern.
        // _tcg_source       = which Picturator import path this line item takes
        // _configuration_id = Picanova itemId, UUID — EMPTY for universal-configurator
        // _tcg_config_id    = UC configurationId (loads the project for editing, and is
        //                     what the UC print pipeline downloads the PDF with)
        // Every name keeps its leading underscore on purpose: a property without one is
        // customer-visible in cart, checkout and the order confirmation.
        var properties = {
          _tcg_app: 'tcg-configurator',
          _tcg_version: '1',
          _tcg_shop: cfg.shopCode || '',
          _tcg_editor: editorType,
          _tcg_source: importSource,
          _sku: sku,
          _PM: (structuredSku && structuredSku.PM) || '',
          _F: (structuredSku && structuredSku.F) || '',
          _configuration_id: picanovaItemId,
          _tcg_config_id: ucConfigId,
          _thumbnail: thumbnail,
        };

        // Add upload key if available (needed for fulfillment)
        if (payload.imageUploadKey) {
          properties._upload_key = payload.imageUploadKey;
        }

        // UC product id and page count. Picturator needs both for the multi-page path: the
        // product id to resolve the UC product, the page count because it drives binding and
        // price. The photobook payload carries them in `configuration`; the
        // tcg-configurator editors put `productId` flat. Emitted only when present — an
        // empty property on every canvas line item would be noise. Requested by Modris
        // (2026-08-11), NGE-10122 §2.2.
        var ucProductId = readPayloadField(payload, 'productId');
        if (ucProductId) {
          properties._tcg_product_id = String(ucProductId);
        }
        var ucPageCount = readPayloadField(payload, 'pageCount');
        if (ucPageCount) {
          properties._tcg_page_count = String(ucPageCount);
        }

        // Finishing SKU codes derived from the structured sku object, split into the two
        // properties the cart labels from — `_add_ons` for the variant's `add_on.products`
        // and `_options` for its `add_on.options`. See splitFinishingSkus for why the
        // fallback direction is `_add_ons`.
        //
        // `_add_on_handles` / `_option_handles` ride along because the tcg-campaigns checkout
        // and account UI extensions read the HANDLES, not the skus (parseCartLineAttributes.ts).
        // Without them the cart would label a finishing choice and checkout would not, so the
        // customer sees their selection vanish at the till.
        //
        // `_add_ons_display` / `_options_display` stay omitted, deliberately: NOTHING reads
        // them (grep-verified across this theme and tcg-campaigns). The checkout and account
        // extensions resolve their labels from the HANDLES, querying
        // `metaobject(handle: {type: "add_on", handle: …})` themselves — see
        // checkout-product-details/src/helpers/queryAddOnOptions.ts. Emitting the display
        // pair would be parity for its own sake.
        if (structuredSku) {
          var finishing = splitFinishingSkus(deriveFinishingSkus(structuredSku), variant);
          if (finishing.addOns) {
            properties._add_ons = finishing.addOns;
          }
          if (finishing.addOnHandles) {
            properties._add_on_handles = finishing.addOnHandles;
          }
          if (finishing.options) {
            properties._options = finishing.options;
          }
          if (finishing.optionHandles) {
            properties._option_handles = finishing.optionHandles;
          }

          // Savings must survive the trip to checkout — see computeOriginalPricePerUnit.
          //
          // The strike is computed over EXACTLY the codes emitted in `_add_ons`, because that
          // is the set the charge is computed over: `channel-price` (run.rs) sums every
          // `_add_ons` entry the tier JSON keys, and line-item-price.liquid's own fallback
          // strikes every `_add_ons` entry the strike JSON keys. Pricing a SUBSET here —
          // only the codes that matched the variant's metaobject list — made a rule-3 code
          // (in neither list, but keyed by the JSON) charged and not struck, so checkout
          // understated the saving and a UC line disagreed with a canvas line in the same
          // order summary (PR #190 review).
          //
          // addToCartData reaches the same invariant from the other side: its `_add_ons` IS
          // the matched list, so its charge and its strike are the same set by construction.
          // The rule both paths obey is "strike over exactly what you emitted", not "strike
          // over what matched".
          var originalPrice = computeOriginalPricePerUnit(
            variant && variant.strikePrice,
            finishing.addOnCodes,
            ucProductId,
            ucPageCount
          );
          if (originalPrice.length) {
            properties._original_price_per_unit = originalPrice;
          }
        }

        var formData = {
          items: [
            {
              id: variant.id,
              quantity: payload.quantity || 1,
              properties: properties,
            },
          ],
        };

        // Add cart-level attributes (market, brand, language)
        if (global.theme) {
          formData.attributes = {
            _market: global.theme.market || '',
            _brand_id: global.theme.brandId || '',
            _language: global.theme.language || '',
          };
          if (global.theme.market_group && global.theme.market_group.index) {
            // NGE-12307: cart attributes are STRINGS — a raw object is coerced to
            // "[object Object]" on every order.
            formData.attributes._market_groups = JSON.stringify(global.theme.market_group.index);
          }
        }

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        })
          .then(function (response) {
            if (!response.ok) {
              return response.text().then(function (text) {
                throw new Error('Cart add failed: ' + text);
              });
            }
            return response.json();
          })
          .then(function (addResult) {
            // Edit mode: when the editor was opened from a cart line ("Edit"), the URL
            // carries ?cartItemId=<old line key>. Remove that original line so the edit
            // REPLACES it instead of leaving a duplicate. Skip removal if the re-add merged
            // back into the same line (identical config → same key), or removal would delete
            // the item we just added.
            var editKey = getUrlParam('cartItemId');
            if (!editKey) return addResult;
            var newKeys = (addResult && addResult.items ? addResult.items : []).map(function (i) {
              return i.key;
            });
            if (newKeys.indexOf(editKey) !== -1) return addResult;
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: editKey, quantity: 0 }),
            })
              .then(function () {
                return addResult;
              })
              .catch(function (e) {
                console.warn(
                  '[TCG Configurator] edit-replace: failed to remove old line',
                  editKey,
                  e
                );
                return addResult;
              });
          });
      }
    );
  }

  /**
   * Handle post-cart redirect based on settings
   */
  function handleCartRedirect(cfg) {
    switch (cfg.shopifyRedirect) {
      case 'checkout':
        global.location.href = '/checkout';
        break;
      case 'cart':
        global.location.href = '/cart';
        break;
      case 'none':
      default:
        break;
    }
  }

  // =========================================================================
  // CUSTOMER TOKEN (NGE-13842)
  // =========================================================================

  /**
   * The only origins a customer token is ever posted to. `appOrigin` comes from the section's
   * `app_url` setting, so without this list anyone who can edit the theme could point the
   * iframe at their own host and collect customer tokens. Other tcg:* messages are unaffected.
   */
  var CUSTOMER_TOKEN_ORIGINS = [
    'https://app.tcg-configurator.picanova.de',
    'https://test-app.tcg-configurator.picanova.de',
  ];

  /**
   * Ask the gateway for a UC customer token bound to the logged-in Shopify customer.
   *
   * WHY. UC files a saved project under the customer whose UC token comes with the save. The
   * iframe is another origin and cannot read this page's storage, so it asks with
   * `tcg:token-refresh` and we answer with `tcg:login-success`. Without that token the project
   * stays in this browser's session only, and the customer can never list it again — which is
   * the bug.
   *
   * The token is minted by UC on request of tcg-webhook-gateway, behind the Shopify App Proxy:
   * the proxy signs `logged_in_customer_id`, so the gateway knows who is asking without anything
   * in this page being trusted. That is also why this is a same-origin call and never a Shop-API login from
   * here (check:configurator forbids one).
   *
   * No cache beyond the in-flight request: UC only asks when it has no token or its token was
   * just rejected (401/403), so handing back a remembered token would return the dead one. The one
   * exception is the prefetch below, which is handed out at most ONCE.
   *
   * Resolves to the `tcg:login-success` payload, or null for a guest, a missing endpoint, or any
   * failure — the caller then does not answer, and UC tells the customer the project is not
   * linked to an account instead of claiming it is.
   */
  function requestCustomerSession(cfg) {
    if (!cfg.customerLoggedIn || !cfg.customerTokenEndpoint) return Promise.resolve(null);
    if (cfg._customerSessionPending) return cfg._customerSessionPending;

    var pending = global
      .fetch(cfg.customerTokenEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
      .then(function (res) {
        if (!res.ok) throw new Error('token endpoint HTTP ' + res.status);
        return res.json();
      })
      .then(function (body) {
        if (!body || typeof body.ucToken !== 'string' || !body.ucToken) {
          throw new Error('token endpoint returned no ucToken');
        }
        // The editor (tcg-configurator #1445, utils/ucCustomerToken.ts) installs the token only
        // with an expiry it can parse — without one it would drop the token and save unlinked.
        if (
          typeof body.ucTokenExpiresAt !== 'string' ||
          !isFinite(Date.parse(body.ucTokenExpiresAt))
        ) {
          throw new Error('token endpoint returned no usable ucTokenExpiresAt');
        }
        // Access token only, never a refresh token: this page is readable by every script on the
        // storefront, and UC simply asks again (tcg:token-refresh) when its token is rejected.
        var payload = { ucToken: body.ucToken, ucTokenExpiresAt: body.ucTokenExpiresAt };
        if (body.customerName) payload.customerName = body.customerName;
        if (body.customerEmail) payload.customerEmail = body.customerEmail;
        return payload;
      })
      .catch(function (err) {
        console.warn('[TCG Configurator] Customer token unavailable:', err && err.message);
        return null;
      })
      .then(function (payload) {
        cfg._customerSessionPending = null;
        return payload;
      });

    cfg._customerSessionPending = pending;
    return pending;
  }

  /**
   * NGE-13842. Mint the customer token while the editor boots, so Save does not wait for it.
   *
   * WHY. UC gives the parent ~5 s to answer `tcg:token-refresh`, then saves unlinked and tells the
   * customer so. Measured on STIT staging 2026-10-02: the first token call after idle took 8.4 s
   * end to end (the gateway route itself 0.99 s), so the customer saw "saved, but not linked"
   * right before the linked save went through. Asking at boot moves that wait to a moment nobody
   * is waiting on.
   *
   * The prefetched payload answers the FIRST ask only, and only while it has more than
   * TOKEN_REUSE_MARGIN_MS left. Every later ask mints afresh: UC asks again only after its token
   * was rejected, and handing that token back would save unlinked a second time.
   */
  var TOKEN_REUSE_MARGIN_MS = 2 * 60 * 1000;

  function prefetchCustomerSession(cfg) {
    requestCustomerSession(cfg).then(function (payload) {
      // An ask that arrived while this was in flight already shared the same request.
      if (!cfg._prefetchSpent) cfg._prefetchedSession = payload;
    });
  }

  function customerSessionForRefresh(cfg) {
    var held = cfg._prefetchedSession;
    cfg._prefetchedSession = null;
    cfg._prefetchSpent = true;
    if (held && Date.parse(held.ucTokenExpiresAt) - Date.now() > TOKEN_REUSE_MARGIN_MS) {
      return Promise.resolve(held);
    }
    return requestCustomerSession(cfg);
  }

  /**
   * NGE-13842. Where a guest goes when UC asks for a login (`tcg:login-required`, sent when a guest
   * presses Save): the Shopify login, and back to THIS editor URL — which carries the autosaved
   * `configurationId` — so the save can be repeated as the logged-in customer.
   *
   * `return_to` is same-origin only (path + query), as Shopify requires. Built from the login URL
   * the section resolves (`/customer_authentication/login` on New Customer Accounts, see
   * tcg-configurator.liquid); null when there is none, and the message is then ignored.
   */
  function loginReturnUrl(loginUrl, location) {
    // A single leading slash only: `//host` or `/\host` would be read as another origin.
    if (!loginUrl || !/^\/(?![\/\\])/.test(loginUrl)) return null;
    var back = (location.pathname || '/').replace(/^\/+/, '/') + (location.search || '');
    return (
      loginUrl +
      (loginUrl.indexOf('?') === -1 ? '?' : '&') +
      'return_to=' +
      encodeURIComponent(back)
    );
  }

  // =========================================================================
  // MESSAGE HANDLING
  // =========================================================================

  function handleMessage(cfg, iframe, event) {
    var data = event.data;
    if (!data || typeof data.type !== 'string' || !data.type.startsWith('tcg:')) return;

    // Validate origin matches the app URL
    var appOrigin;
    try {
      appOrigin = new URL(cfg.appUrl).origin;
    } catch (_) {
      return;
    }
    if (event.origin !== appOrigin) return;

    console.log('[TCG Configurator]', data.type, data.payload || '');

    switch (data.type) {
      case 'tcg:ready':
        // Editor loaded — could show the iframe / hide loading spinner
        break;

      case 'tcg:add-to-cart':
        addToShopifyCart(cfg, data.payload || {})
          .then(function () {
            // Notify iframe of success
            iframe.contentWindow.postMessage(
              {
                type: 'tcg:cart-result',
                success: true,
              },
              appOrigin
            );
            handleCartRedirect(cfg);
          })
          .catch(function (err) {
            console.error('[TCG Configurator] Cart add failed:', err);
            iframe.contentWindow.postMessage(
              {
                type: 'tcg:cart-result',
                success: false,
                error: err.message,
              },
              appOrigin
            );
          });
        break;

      case 'tcg:checkout':
        // Same as add-to-cart, use configured redirect (default: cart)
        addToShopifyCart(cfg, data.payload || {})
          .then(function () {
            iframe.contentWindow.postMessage(
              {
                type: 'tcg:cart-result',
                success: true,
              },
              appOrigin
            );
            handleCartRedirect(cfg);
          })
          .catch(function (err) {
            console.error('[TCG Configurator] Checkout failed:', err);
            iframe.contentWindow.postMessage(
              {
                type: 'tcg:cart-result',
                success: false,
                error: err.message,
              },
              appOrigin
            );
          });
        break;

      case 'tcg:redirect':
        // Picturator-style redirect — for backward compat, go to checkout
        if (data.url) {
          global.location.href = data.url;
        }
        break;

      case 'tcg:project-saved':
        // Update URL with configurationId for browser back/refresh
        if (data.payload && data.payload.configurationId) {
          try {
            var url = new URL(global.location.href);
            url.searchParams.set('configurationId', data.payload.configurationId);
            global.history.replaceState(null, '', url.toString());
          } catch (_) {}
        }
        break;

      case 'tcg:resize':
        // Auto-resize iframe to content height, but never past the bottom of the screen.
        //
        // Unclamped, this re-introduced NGE-11841 after the initial fit: the app reports the height
        // its own content wants (measured: 710px on a 664px viewport) and that was applied
        // verbatim, pushing the strip holding its primary CTA back below the fold. The page cannot
        // scroll to recover it — the section locks `body { overflow: hidden }` because the editor
        // is supposed to scroll inside the iframe.
        //
        // The app scrolls its own inner container, so a smaller box costs it nothing.
        //
        // `room > 200` here is a MEASUREMENT-TRUST guard, unlike the same literal in
        // resizeIframe(), where it means "the iframe's top is essentially off-screen". When room
        // is that small the layout is mid-settle and clamping to it would collapse the editor, so
        // the app's height is applied unclamped — deliberately accepting the original bug's
        // behaviour in that corner: it is reachable only if whatever sits above the iframe grows
        // to within 200px of the bottom of a short viewport, and a wrong tiny box is worse than a
        // tall one there.
        if (data.height && data.height > 100) {
          var room = global.innerHeight - iframe.getBoundingClientRect().top;
          var height = room > 200 ? Math.min(data.height, room) : data.height;
          iframe.style.height = height + 'px';
          // height is already clamped to room, so it IS the floor — no second Math.min.
          if (room > 200) iframe.style.minHeight = height + 'px';
        }
        break;

      case 'tcg:close':
        global.history.back();
        break;

      case 'tcg:token-refresh':
        // NGE-13842. The reply carries a customer credential, so the origin check above is not
        // enough on its own: another frame served from the same UC host (a second editor, a
        // preview) would pass it. Answer only the iframe this instance created.
        if (!iframe.contentWindow || event.source !== iframe.contentWindow) break;
        if (CUSTOMER_TOKEN_ORIGINS.indexOf(appOrigin) === -1) break;
        customerSessionForRefresh(cfg).then(function (payload) {
          // A guest or a failed mint gets NO reply — UC times out and says "not linked".
          if (!payload || !iframe.contentWindow) return;
          iframe.contentWindow.postMessage(
            { type: 'tcg:login-success', payload: payload },
            appOrigin
          );
        });
        break;

      case 'tcg:login-required':
        // NGE-13842. A guest pressed Save. Only our own iframe may send the customer away, and a
        // logged-in customer is never redirected (that would loop if their token failed).
        if (!iframe.contentWindow || event.source !== iframe.contentWindow) break;
        if (cfg.customerLoggedIn) break;
        var loginUrl = loginReturnUrl(cfg.customerLoginUrl, global.location);
        if (loginUrl) global.location.href = loginUrl;
        break;
    }
  }

  // =========================================================================
  // INIT / DESTROY
  // =========================================================================

  function init(config) {
    var cfg = Object.assign(
      {
        containerId: null,
        appUrl: '',
        apiUrl: '',
        editorType: 'photobook',
        shopCode: '',
        productCode: '',
        locale: '',
        currency: '',
        country: '',
        shopifyRedirect: 'checkout',
        shopifyCustomerId: '',
        customerLoggedIn: false,
        customerTokenEndpoint: '',
        customerLoginUrl: '',
        minHeight: 700,
        skuVariantPairs: [],
      },
      config || {}
    );

    if (!cfg.containerId) throw new Error('TCGConfigurator.init: containerId required');
    if (!cfg.appUrl) throw new Error('TCGConfigurator.init: appUrl required');

    var container = global.document.getElementById(cfg.containerId);
    if (!container)
      throw new Error('TCGConfigurator.init: container "' + cfg.containerId + '" not found');

    // Build SKU→variant lookup map
    cfg._skuVariantMap = {};
    (cfg.skuVariantPairs || []).forEach(function (pair) {
      if (pair.sku && pair.id) {
        cfg._skuVariantMap[pair.sku] = pair.id;
        cfg._skuVariantMap[pair.sku.replace(/-/g, '')] = pair.id;
      }
    });

    // Create iframe
    var iframe = global.document.createElement('iframe');
    iframe.style.cssText =
      'width:100%;border:none;display:block;min-height:' + cfg.minHeight + 'px;';
    iframe.setAttribute('allow', 'camera; clipboard-write; clipboard-read');
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.src = buildIframeSrc(cfg);

    // Clear container and insert iframe
    container.innerHTML = '';
    container.appendChild(iframe);

    // The space actually on screen below the iframe's top edge. Everything below depends on it,
    // so it is computed in one place.
    function availableHeight() {
      return global.innerHeight - iframe.getBoundingClientRect().top;
    }

    // Dynamic height: fill remaining viewport below the iframe's top edge.
    //
    // `min-height` is set here too, and that is the whole fix for NGE-11841. The inline
    // `min-height: <cfg.minHeight>px` below is a FLOOR, so on a phone it overrode the height this
    // function computes: measured on prod at 390x664, available came out 530px and this function
    // set it, while the 700px floor won and the box rendered 700 tall — 170px below the fold with
    // `body.tcg-configurator-active { overflow: hidden }` blocking any gesture that could reach
    // them. On the starmap order summary that put "Aggiungi al carrello" half off the screen.
    //
    // So the floor is clamped to what is on screen. It still does its original job — keeping the
    // iframe from collapsing while the app boots, and holding a usable height on a desktop where
    // `available` is large — it just cannot exceed the viewport any more.
    function resizeIframe() {
      var available = availableHeight();
      if (available > 200) {
        iframe.style.height = available + 'px';
      }
      // Math.min, not a conditional: on desktop `available` exceeds minHeight and nothing changes.
      iframe.style.minHeight = Math.min(cfg.minHeight, Math.max(available, 200)) + 'px';
    }
    resizeIframe();
    global.addEventListener('resize', resizeIframe);
    // Re-check after Shopify header/banners finish rendering
    setTimeout(resizeIframe, 300);
    setTimeout(resizeIframe, 1000);

    // Setup message listener
    var onMsg = function (e) {
      handleMessage(cfg, iframe, e);
    };
    global.addEventListener('message', onMsg);

    // Same gates as the answer itself: a logged-in customer and an allowlisted UC host.
    var bootOrigin = '';
    try {
      bootOrigin = new URL(cfg.appUrl).origin;
    } catch (_) {}
    if (
      cfg.customerLoggedIn &&
      cfg.customerTokenEndpoint &&
      CUSTOMER_TOKEN_ORIGINS.indexOf(bootOrigin) !== -1
    ) {
      prefetchCustomerSession(cfg);
    }

    // Store instance for cleanup
    instances.set(cfg.containerId, {
      iframe: iframe,
      onMessage: onMsg,
      config: cfg,
      resizeHandler: resizeIframe,
    });

    console.log('[TCG Configurator] Initialized:', cfg.editorType, iframe.src);
  }

  function destroy(containerId) {
    var inst = instances.get(containerId);
    if (!inst) return;
    global.removeEventListener('message', inst.onMessage);
    if (inst.resizeHandler) global.removeEventListener('resize', inst.resizeHandler);
    if (inst.iframe && inst.iframe.parentNode) {
      inst.iframe.parentNode.removeChild(inst.iframe);
    }
    instances.delete(containerId);
  }

  // Export to global
  global.TCGConfigurator = {
    init: init,
    destroy: destroy,

    // Pure, side-effect-free helpers, exposed ONLY so tests/tcg-configurator-finishing.test.mjs
    // can pin the money-adjacent classification rules (see splitFinishingSkus). Nothing in
    // the theme calls these, and nothing outside the test should.
    //
    // Why exposure rather than `import`: this file is a rollup ENTRY with zero imports, and
    // adding one would let rollup hoist the shared code into a separate chunk. Chunk URLs are
    // year-cached and a relative import once served a stale one for months (NGE-10869, which
    // is why `check:chunks` exists). A property on the existing export cannot move the module
    // graph, so the test gets its seam and the bundle keeps its shape.
    _internals: {
      readAddOnEntries: readAddOnEntries,
      deriveFinishingSkus: deriveFinishingSkus,
      splitFinishingSkus: splitFinishingSkus,
      computeOriginalPricePerUnit: computeOriginalPricePerUnit,
      computePageSurcharge: computePageSurcharge,
      productScopedPriceKey: productScopedPriceKey,
      // NGE-13453 — the index-backed lookup. Exposed so the tests can assert on the QUERY
      // that goes out, not just on the variant that comes back: for a listed product both
      // paths return the same variant, so a result-only assertion cannot tell whether the
      // search index was consulted.
      configuratorHandlesFor: configuratorHandlesFor,
      pickVariantNode: pickVariantNode,
      findVariantBySku: findVariantBySku,
      // NGE-13842 — the tests also drive it through init() and a captured message listener, so
      // the origin/source checks are covered; this seam is for the mint's own branches.
      requestCustomerSession: requestCustomerSession,
      loginReturnUrl: loginReturnUrl,
    },
  };
})(typeof window !== 'undefined' ? window : this);
